import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStore } from '../../store';
import DueDiligence from '../DueDiligence';
import Poa from '../Poa';
import LeaseReg from '../LeaseReg';
import Workflows from '../Workflows';
import OverflowChips from '../OverflowChips';
import { RC, rcScope } from '../../utils/fonts';

// SLF Property = the firm's legal console: Due Diligence, POA (Power of Attorney), Lease
// Registration (read-only mirror of slf-legal.co's fee assessments) and Legal Matters.
//
// Legal Matters replaced the empty "Property Transfer" tab on 2026-09-22 (owner: Property Transfer
// 这个是空的，可以删除). It is the Service Workflows engine narrowed to the 'property' desk and
// grouped into families — Contract Review, Draft Contracts, Mortgage/Servitude, Transfer of
// Ownership, Last Wills, Notary, Translation — so all ten services the firm offers are managed from
// this one page. Transfer of Ownership, which the placeholder stood for, is one of those families.
// Since 2026-09-23 the same console serves SLF™ Visa / Company / License (components/ServiceDesk);
// the desk table lives in server/src/services/serviceDesks.ts.
//
// ⛔ The first three tabs are untouched, files and screens alike (owner 2026-09-22: Due Diligence /
// POA / Lease Registration 这三个一点都不能动，包括界面). Everything new lives in the fourth.
type SPTab = 'due_diligence' | 'poa' | 'lease_reg' | 'legal_matters';

export default function SlfProperty() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me } = useStore();
  const [params, setParams] = useSearchParams();

  // Legal Matters mints no permission of its own — it IS the Service Workflows engine, so
  // `workflows` opens it. `property_transfer` opens it too: that key was minted for the Coming-soon
  // tab this one replaces, and its service is now one of the families here, so the three people
  // holding it keep the door they already had rather than quietly losing it.
  const can = (k: SPTab) => me?.role === 'admin' || (me?.modules || []).includes(k)
    || (k === 'legal_matters' && (me?.modules || []).some(m => m === 'workflows' || m === 'property_transfer'));
  const TABS: { key: SPTab; label: string }[] = [
    { key: 'due_diligence', label: t('mod_due_diligence') },
    { key: 'poa', label: t('mod_poa') },
    { key: 'lease_reg', label: t('mod_lease_reg') },
    { key: 'legal_matters', label: t('mod_legal_matters') },
  ];
  const visible = TABS.filter(x => can(x.key));
  const param = params.get('v') as SPTab | null;
  const [tab, setTab] = useState<SPTab>(param && visible.some(v => v.key === param) ? param : (visible[0]?.key || 'due_diligence'));
  const select = (k: SPTab) => { setTab(k); setParams(p => { const n = new URLSearchParams(p); n.set('v', k); return n; }, { replace: true }); };

  const tabBtn = (k: SPTab, label: string) => (
    <button key={k} onClick={() => select(k)} style={{ padding: '8px 18px', fontSize: 13.5, fontWeight: 700, fontFamily: RC, cursor: 'pointer', border: 'none', background: 'transparent', color: tab === k ? 'var(--accent)' : 'var(--text-tertiary)', borderBottom: `3px solid ${tab === k ? 'var(--accent)' : 'transparent'}`, whiteSpace: 'nowrap', flexShrink: 0 }}>{label}</button>
  );

  // Legal Matters' service families live in THIS bar (owner 2026-09-25) instead of a strip of their
  // own below it, so the work area keeps that row. The selection is held here and handed down, which
  // is why Workflows stops drawing its own chips: one set on screen, one piece of state behind it.
  // Whatever does not fit folds into a dropdown at the end of the line — and if the folded part
  // holds the SELECTED family, that button wears its name, so a filtered list is never unexplained.
  // Typography follows Due Diligence (owner: 字体全部按照 Due Diligence 里的字体来显示); the colours
  // stay this console's green.
  const [fam, setFam] = useState('');
  // Legal Matters keeps every family on the bar. The owner narrowed the three service DESKS to the
  // families they have actually worked (SLF Company / Visa / License, 2026-09-25) and named only
  // those; this console is left as it is until he says otherwise.
  const [families, setFamilies] = useState<{ key: string; label: string; priced: boolean; used?: number }[]>([]);
  const chipBase: React.CSSProperties = { padding: '5px 12px', fontSize: 12.5, fontWeight: 600, borderRadius: 20, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)' };
  const chipOn: React.CSSProperties = { border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff' };
  const famChips = [
    { key: '__all', label: t('wf_all'), active: !fam, onClick: () => setFam('') },
    ...families.map(f => ({
      key: f.key,
      // A family the price list has not caught up with keeps its chip, marked, rather than vanishing.
      label: f.priced ? f.label : `${f.label} ·`,
      active: fam === f.key,
      muted: !f.priced,
      title: f.priced ? undefined : t('lm_not_priced'),
      onClick: () => setFam(f.key),
    })),
  ];

  return (
    <div className="slf-property" style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      {/* ONE typeface for the whole console (owner 2026-09-25: 请确保这个 SLF Property 模块下所有页面的
          文字都是一致的). Due Diligence sets Roboto Condensed on every element of its own because it was
          rebuilt to match the form it replaced; POA, Lease Registration and Legal Matters were still on
          the app's default face, so moving between tabs changed the type under you.
          Declared once here and inherited, rather than added to a few hundred inline styles across four
          modules. Form controls need saying explicitly — buttons, inputs, selects and textareas do not
          inherit font-family from their container. Icons are safe: `.ti` matches the <i> itself, and a
          rule that matches an element always beats a value inherited from its parent. */}
      <style>{rcScope('slf-property')}</style>
      {/* ONE row, not two (owner 2026-09-22: 现在是2排，请整合到同一行，那么下面的操作界面可以变大一些).
          The title bar and the tab strip were stacked, costing ~48px of every screen in here — and
          Due Diligence is the tallest form in the system. They share a bar now: `stretch` lets each
          tab fill the bar's height, so the active underline still lands exactly on the bar's own
          bottom border rather than floating above it. */}
      <header style={{ display: 'flex', alignItems: 'stretch', gap: 12, minHeight: 40, padding: '0 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <button onClick={() => navigate('/')} title="SLF" style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img className="app-logo" src="/logo.png" alt="SLF" style={{ width: 28, height: 28, objectFit: 'contain' }} />
          </button>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{t('mod_slf_property')}</div>
        </div>
        <div style={{ width: 1, background: 'var(--border)', flexShrink: 0, margin: '9px 0' }} aria-hidden />
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {visible.map(v => tabBtn(v.key, v.label))}
        </div>
        {tab === 'legal_matters' && (
          <>
            <div style={{ width: 1, background: 'var(--border)', flexShrink: 0, margin: '9px 0' }} aria-hidden />
            <OverflowChips chips={famChips} chipStyle={chipBase} activeStyle={chipOn} mutedColor="var(--text-tertiary)" font={RC} style={{ alignSelf: 'center' }} />
          </>
        )}
      </header>

      {tab === 'due_diligence' ? <DueDiligence embedded />
        : tab === 'poa' ? <Poa embedded />
        : tab === 'lease_reg' ? <LeaseReg embedded />
        : <Workflows embedded desk="property" famCtl={{ fam, setFam }} onFamilies={setFamilies} />}
    </div>
  );
}
